"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Layers, Ruler, ShieldCheck, Umbrella } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const capabilities = [{
  icon: Layers,
  title: "Wood & Composite Decking",
  description: "Premium pressure-treated pine, natural cedar, and low-maintenance capped composite boards engineered for durability in Georgia humidity."
}, {
  icon: Ruler,
  title: "Multi-Level Architectures",
  description: "Custom grade transitions, integrated wide-tread staircases, and dedicated dining or lounge landings tailored to your yard's topography."
}, {
  icon: ShieldCheck,
  title: "Architectural Railings",
  description: "Sleek stainless steel horizontal cable, matte black powder-coated aluminum, and classic craftsman timber post configurations."
}, {
  icon: Umbrella,
  title: "Under-Deck Dry Systems",
  description: "Integrated water diversion ceilings with gutter channels, finished soffits, and recessed lighting to create usable dry lower patios."
}];
const structuralMetrics = [{
  label: "Framing standard",
  value: "Heavy-duty joist & ledger flashing"
}, {
  label: "Code compliance",
  value: "100% City of Decatur approved"
}, {
  label: "Fasteners",
  value: "Concealed stainless steel clip systems"
}];
export default function DeckRemodeling() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="deck-remodeling" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-start">
          {/* Left Column: Editorial & Capability Stack */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={containerVariants} className="space-y-6">
              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <Badge variant="outline" className="bg-secondary/70 text-foreground border-border text-xs uppercase tracking-wider font-semibold px-3 py-1 rounded-md">
                  Outdoor Living & Decks
                </Badge>
                <div className="h-px w-12 bg-border hidden sm:block" />
              </motion.div>

              <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Custom decks built for Decatur living
              </motion.h2>

              <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Expand your home into the outdoors with durable composite decking,
                multi-level structural framing, and weather-resistant finishes built
                to withstand Georgia seasons with minimal upkeep.
              </motion.p>

              {/* Capability Cards Stack */}
              <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {capabilities.map((item, i) => {
                const Icon = item.icon;
                return <motion.div key={item.title} data-index={i} whileHover={shouldReduceMotion ? {} : {
                  y: -4
                }} transition={{
                  duration: 0.3,
                  ease: "easeOut"
                }} className="bg-card/85 backdrop-blur-md border border-border rounded-xl p-5 sm:p-6 shadow-xs hover:shadow-md hover:border-primary/40 transition-[background-color,border-color,box-shadow]">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center mb-4 text-primary border border-border">
                        <Icon className="size-5" />
                      </div>
                      <h3 className="font-sans text-base font-semibold text-foreground mb-2">
                        {item.title}
                      </h3>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </motion.div>;
              })}
              </motion.div>

              {/* CTA Action Bar */}
              <motion.div variants={itemVariants} className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-7 py-6 text-sm font-semibold shadow-sm transition-all">
                  <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                    Schedule structural consultation
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-secondary rounded-lg px-6 py-6 text-sm font-medium transition-colors">
                  <Link to="/ProjectsPage">View recent builds</Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column: Architectural Photography & Overlapping Callout */}
          <div className="lg:col-span-5 relative mt-4 lg:mt-0">
            <motion.div initial={{
            opacity: shouldReduceMotion ? 1 : 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative rounded-2xl overflow-hidden border border-border shadow-md bg-muted">
              <div className="aspect-4/5 sm:aspect-4/3 lg:aspect-4/5 w-full relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/deck_remodeling_showcase.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/deck_remodeling_showcase.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/deck_remodeling_showcase.png-wvc-sizes" alt="Multi-level custom composite deck with cable railings built in Decatur" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-linear-to-t from-foreground/30 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Decorative Accent Hairline */}
              <div className="absolute top-0 inset-x-0 h-1 bg-linear-to-r from-primary via-primary/60 to-transparent" />
            </motion.div>

            {/* Overlapping Translucent Frosted Glass Plate */}
            <motion.div initial={{
            opacity: shouldReduceMotion ? 1 : 0,
            y: shouldReduceMotion ? 0 : 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.5,
            delay: 0.2,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative sm:absolute sm:-bottom-8 sm:-left-8 lg:-bottom-6 lg:-left-10 mt-6 sm:mt-0 sm:max-w-xs md:max-w-sm w-full bg-card/90 backdrop-blur-xl border border-border rounded-xl p-5 sm:p-6 shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <div className="size-2.5 rounded-full bg-primary" />
                <span className="font-sans text-xs uppercase tracking-wider font-bold text-foreground">
                  Craftsmanship Standards
                </span>
              </div>

              <div className="space-y-3 divide-y divide-border/60">
                {structuralMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="pt-2.5 first:pt-0 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span className="font-sans text-xs text-muted-foreground">
                        {metric.label}
                      </span>
                    </div>
                    <span className="font-sans text-xs font-semibold text-foreground text-right">
                      {metric.value}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}